import { useEffect, useState } from 'react';
import { Subject } from 'rxjs';
import { Breakpoint } from '@mui/system';

import { SelectDialog } from '/shared/SelectDialog';
import { handleEnterPress } from '/helpers';
import { SyntheticEventWithMetaKey } from '/common/models';

import { ConfirmDialogType, IConfirmDialogProps, IConfirmProps } from './types';
import { ConfirmDialogActions, ConfirmDialogHeader } from './components';
import { ConfirmDialogContent } from './components/ConfirmDialogContent';
import { getConfirmDialogTyped, useConfirmDialogColor } from './helpers';

const subject = new Subject<IConfirmDialogProps>();

export const confirmDialog = (props: IConfirmProps) => {
  subject.next({ ...props, open: true });
};

export const ConfirmDialogService = () => {
  const [props, setProps] = useState<IConfirmDialogProps>({
    open: false,
  });

  const {
    open,
    title,
    description,
    content,
    type = ConfirmDialogType.Confirm, // Default type
    entityType,
    entityName,
    maxWidth = 'sm',
    closeButton,
    successButton,
    onClose,
    afterClose,
    onSuccess,
    TransitionComponent,
  } = props;

  const defaults = getConfirmDialogTyped({ type, entityType, entityName, description });

  const finalTitle = title ?? defaults.title;
  const mainColor = useConfirmDialogColor(type);

  useEffect(() => {
    const sub = subject.subscribe((nextProps: IConfirmDialogProps) => {
      setProps(nextProps);
    });
    return () => sub.unsubscribe();
  }, []);

  const closeDialog = () => {
    setProps((p) => ({ ...p, open: false }));
  };
  const dialogContent = typeof content === 'function' ? content?.(closeDialog) : content;

  const handleCloseClick = () => {
    if (closeButton?.onClick) {
      closeButton.onClick(closeDialog);
    } else {
      closeDialog();
      if (onClose) {
        onClose();
      }
      if (afterClose) {
        afterClose();
      }
    }
  };

  const handleSuccessClick = (event?: SyntheticEventWithMetaKey) => {
    if (successButton?.onClick) {
      successButton.onClick(event);
    } else {
      if (onSuccess) {
        onSuccess();
      }
      if (afterClose) {
        afterClose();
      }
    }
    closeDialog();
  };

  return (
    <SelectDialog
      data-testid='confirm-dialog-wrapper'
      Header={
        <ConfirmDialogHeader type={type} title={finalTitle} maxWidth={maxWidth as Breakpoint} mainColor={mainColor} />
      }
      CustomActions={
        <ConfirmDialogActions
          closeButton={closeButton}
          successButton={successButton}
          onClose={handleCloseClick}
          onSuccess={handleSuccessClick}
          mainColor={mainColor}
          confirmLabelDefault={defaults.confirmLabel}
          cancelLabelDefault={defaults.cancelLabel}
        />
      }
      maxWidth={maxWidth}
      fullWidth={true}
      Content={<ConfirmDialogContent mainColor={mainColor} dialogContent={dialogContent} typedContent={defaults} />}
      open={open}
      onClose={closeDialog}
      onKeyUp={handleEnterPress(handleCloseClick)}
      TransitionComponent={TransitionComponent}
    />
  );
};
